import React from "react";
import { Skeleton, Box, Card, CardContent, Stack } from "@mui/material";

const Load1 = () => {
  return (
    <div className="border mt-2.5">
      <div className="bg-white rounded-lg p-3">
        {/* Header Section */}
        <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
        <Skeleton variant="text" width={120} height={20} sx={{ mb: 3 }} />

        {/* Dispatch Info Header */}
        <Skeleton
          variant="rectangular"
          width="100%"
          height={40}
          sx={{ mb: 2, borderRadius: 1 }}
        />

        {/* Dispatch Buttons or Info */}
        <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
          <Skeleton
            variant="rectangular"
            width={100}
            height={32}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={90}
            height={32}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={120}
            height={32}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={130}
            height={32}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={60}
            height={32}
            sx={{ borderRadius: 1 }}
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
          <Skeleton
            variant="rectangular"
            width={50}
            height={28}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={80}
            height={28}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={70}
            height={28}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={120}
            height={28}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={100}
            height={28}
            sx={{ borderRadius: 1 }}
          />
        </Box>

        {/* Status Section */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
          <Skeleton
            variant="rectangular"
            width={120}
            height={36}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={60}
            height={36}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={80}
            height={36}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton variant="text" width={100} height={20} />
          <Skeleton variant="text" width={90} height={20} />
        </Box>

        {/* Load Status Tracker Skeleton */}

        {/* Pickup Section */}
        <StopSectionSkeleton title="PICKUP - STOP#1" />

        {/* Delivery Section */}
        <StopSectionSkeleton title="DELIVERY - STOP#2" />
      </div>
    </div>
  );
};

const LoadStatusTrackerSkeleton = () => {
  return (
    <Box sx={{ mb: 3 }}>
      {/* Status Cards Skeleton */}
      {[1, 2].map((item) => (
        <Card
          key={item}
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            mb: 2,
          }}
        >
          {/* Header with Progress */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              p: 2,
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Skeleton
                variant="text"
                width={150}
                height={24}
                sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
              />
              <Skeleton
                variant="text"
                width={80}
                height={20}
                sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
              />
            </Box>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={6}
              sx={{ borderRadius: 3, bgcolor: "rgba(255,255,255,0.3)" }}
            />
          </Box>

          <CardContent sx={{ p: 2 }}>
            {/* Current Status */}
            <Box sx={{ mb: 2 }}>
              <Skeleton variant="text" width={100} height={16} sx={{ mb: 1 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Skeleton variant="circular" width={36} height={36} />
                <Box>
                  <Skeleton
                    variant="rectangular"
                    width={120}
                    height={24}
                    sx={{ borderRadius: 12, mb: 0.5 }}
                  />
                  <Skeleton variant="text" width={80} height={14} />
                </Box>
              </Box>
            </Box>

            {/* Timeline Information */}
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Box flex={1}>
                  <Skeleton
                    variant="text"
                    width={100}
                    height={14}
                    sx={{ mb: 0.5 }}
                  />
                  <Skeleton
                    variant="text"
                    width={180}
                    height={16}
                    sx={{ mb: 0.5 }}
                  />
                  <Skeleton variant="text" width={60} height={12} />
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Box flex={1}>
                  <Skeleton
                    variant="text"
                    width={80}
                    height={14}
                    sx={{ mb: 0.5 }}
                  />
                  <Skeleton variant="text" width={160} height={16} />
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

const StopSectionSkeleton = ({ title }: any) => {
  return (
    <div className="border mb-3">
      {/* Section Header */}
      <div className="bg-gray-100 p-2">
        <Skeleton variant="text" width={150} height={20} />
      </div>

      <div className="flex gap-4 p-2">
        {/* Left Column */}
        <div className="flex-1">
          {/* Date Section */}
          <div className="mb-3">
            <Skeleton variant="text" width={40} height={16} sx={{ mb: 1 }} />
            <Skeleton variant="text" width={200} height={14} />
          </div>

          {/* Address Section */}
          <div className="mb-3">
            <Skeleton variant="text" width={120} height={16} sx={{ mb: 1 }} />
            <Skeleton
              variant="text"
              width="100%"
              height={14}
              sx={{ mb: 0.5 }}
            />
            <Skeleton variant="text" width="80%" height={14} />
          </div>

          {/* Location Notes */}
          <div className="mb-3">
            <Skeleton variant="text" width={100} height={16} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="90%" height={14} />
          </div>

          {/* Instructions */}
          <div className="mb-3">
            <Skeleton variant="text" width={80} height={16} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="85%" height={14} />
          </div>

          {/* Stop Info */}
          <div className="mb-3">
            <Skeleton variant="text" width={150} height={16} sx={{ mb: 1 }} />
            <Skeleton
              variant="rectangular"
              width={100}
              height={24}
              sx={{ borderRadius: 1 }}
            />
          </div>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Skeleton
              variant="rectangular"
              width={30}
              height={24}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width={35}
              height={24}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width={60}
              height={24}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width={90}
              height={24}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        </div>

        {/* Right Column */}
        <div className="flex-1">
          {/* Quantity Info */}
          <div className="mb-5">
            <Stack spacing={0.5}>
              <Skeleton variant="text" width={150} height={14} />
              <Skeleton variant="text" width={140} height={14} />
              <Skeleton variant="text" width={120} height={14} />
              <Skeleton variant="text" width={130} height={14} />
            </Stack>
          </div>

          {/* Reference Numbers */}
          <div className="mb-3">
            <Stack spacing={0.5}>
              <Skeleton variant="text" width={180} height={14} />
              <Skeleton variant="text" width={140} height={14} />
              <Skeleton variant="text" width={120} height={14} />
              <Skeleton variant="text" width={100} height={14} />
            </Stack>
          </div>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Skeleton
              variant="rectangular"
              width={50}
              height={24}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width={40}
              height={24}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width={100}
              height={24}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        </div>
      </div>
    </div>
  );
};

const LoadSideBarSkeleton = () => {
  return (
    <>
      <Load1 />
      {/* <LoadStatusTrackerSkeleton /> */}
      <StopSectionSkeleton />
    </>
  );
};

export default LoadSideBarSkeleton;
